// Mode + location color mapping for the month calendar. Kept SEPARATE from
// statusMeta.ts so the calendar's mode/location layer never collides with the
// status badge palette (Scheduled/Completed/etc. still own those colors).
//
// Rules:
//  • Online mode overrides location (always blue).
//  • Known in-person locations get a stable brand color (case/space insensitive).
//  • Unknown locations fall back to a neutral slate.

import { alpha } from "@mui/material";

export const ONLINE_COLOR = "#2563eb";

export const LOCATION_PALETTE: Record<string, string> = {
  ajax: "#059669",
  scarborough: "#7c3aed",
  pickering: "#d97706",
  oshawa: "#e11d48",
  whitby: "#4f46e5",
};

export const UNKNOWN_LOCATION_COLOR = "#64748b";

const normalize = (s: string | null | undefined): string =>
  String(s || "").trim().toLowerCase();

export interface EventColor {
  border: string;
  bg: string;
  text: string;
}

/**
 * Compute { border, bg, text } for a schedule row. Online mode always wins
 * over location. Location match is normalized (lowercase + trimmed) and only
 * checks whether the row's location NAME contains one of the palette keys.
 */
export function eventColor(row: {
  classMode?: string | null;
  locationName?: string | null;
}): EventColor {
  const mode = normalize(row?.classMode);
  if (mode === "online") {
    return { border: ONLINE_COLOR, bg: alpha(ONLINE_COLOR, 0.12), text: "#0f172a" };
  }
  const loc = normalize(row?.locationName);
  let color = UNKNOWN_LOCATION_COLOR;
  if (loc) {
    for (const key of Object.keys(LOCATION_PALETTE)) {
      if (loc.includes(key)) {
        color = LOCATION_PALETTE[key];
        break;
      }
    }
  }
  return { border: color, bg: alpha(color, 0.12), text: "#0f172a" };
}

/**
 * Returns a stable legend key + label for a row (used by LegendBar to build
 * the "only what's visible" list).
 */
export function legendEntryFor(row: {
  classMode?: string | null;
  locationName?: string | null;
}): { key: string; label: string; color: string } {
  const mode = normalize(row?.classMode);
  if (mode === "online") {
    return { key: "online", label: "Online", color: ONLINE_COLOR };
  }
  const loc = normalize(row?.locationName);
  if (loc) {
    for (const key of Object.keys(LOCATION_PALETTE)) {
      if (loc.includes(key)) {
        const label = key.charAt(0).toUpperCase() + key.slice(1);
        return { key, label, color: LOCATION_PALETTE[key] };
      }
    }
    return { key: `loc:${loc}`, label: row?.locationName || "Other", color: UNKNOWN_LOCATION_COLOR };
  }
  return { key: "unknown", label: "Unknown", color: UNKNOWN_LOCATION_COLOR };
}
